// Transform state management and coordinate conversion utilities
import type { TransformState, Point } from "../types/stage-types.js";

/**
 * Creates a transform state manager with dynamic content centering support
 * @param initialTransform - Initial transform values
 * @param getStageSize - Function to get current stage dimensions
 * @param getContentSize - Function to get current content dimensions for centering
 * @returns Transform state manager API
 */
export const createTransformManager = (
  initialTransform: Partial<TransformState> = {},
  getStageSize?: () => { width: number; height: number },
  getContentSize?: () => { width: number; height: number } | null
) => {
  const defaultTransform: TransformState = {
    scale: 1,
    translateX: 0,
    translateY: 0,
    rotation: 0,
  };

  let currentTransform: TransformState = {
    ...defaultTransform,
    ...initialTransform,
  };

  let listeners: Array<(transform: TransformState) => void> = [];

  // Store the base centering offset (calculated once, not on every transform)
  let baseCenterOffset: { x: number; y: number } = { x: 0, y: 0 };
  let isCenterCalculated = false;

  /**
   * Calculate and store the base center offset (should only be called once or on resize/content change)
   */
  const calculateBaseCenterOffset = (): void => {
    if (!getStageSize || !getContentSize) {
      baseCenterOffset = { x: 0, y: 0 };
      return;
    }

    const stage = getStageSize();
    const content = getContentSize();

    if (!content) {
      baseCenterOffset = { x: 0, y: 0 };
      return;
    }

    const centerX = (stage.width - content.width) / 2;
    const centerY = (stage.height - content.height) / 2;

    baseCenterOffset = { x: centerX, y: centerY };
    isCenterCalculated = true;
  };

  /**
   * Get the current base center offset (calculates if not yet done)
   */
  const getBaseCenterOffset = (): { x: number; y: number } => {
    if (!isCenterCalculated) {
      calculateBaseCenterOffset();
    }
    return baseCenterOffset;
  };

  /**
   * Get current transform state (immutable copy)
   */
  const getTransform = (): TransformState => ({ ...currentTransform });

  /**
   * Update transform state
   * @param newTransform - Partial transform to merge
   */
  const setTransform = (newTransform: Partial<TransformState>): void => {
    currentTransform = { ...currentTransform, ...newTransform };

    // Notify all listeners of the change
    listeners.forEach((listener) => listener(currentTransform));
  };

  /**
   * Reset transform to default values
   */
  const resetTransform = (): void => {
    setTransform(defaultTransform);

    // Force a re-center after DOM has updated from the reset
    if (getStageSize && getContentSize) {
      requestAnimationFrame(() => {
        // Trigger listeners again to force re-centering with correct dimensions
        listeners.forEach((listener) => listener(currentTransform));
      });
    }
  };

  /**
   * Add a listener for transform changes
   * @param listener - Function to call when transform changes
   * @returns Unsubscribe function
   */
  const onTransformChange = (
    listener: (transform: TransformState) => void
  ): (() => void) => {
    listeners.push(listener);

    // Return unsubscribe function
    return () => {
      const index = listeners.indexOf(listener);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  };

  /**
   * Generate CSS transform string from current state
   */
  const toCSSTransform = (): string => {
    const { scale, translateX, translateY, rotation } = currentTransform;

    // Apply centering only if both stage and content size functions are provided
    if (getStageSize && getContentSize) {
      const centerOffset = getBaseCenterOffset();
      const finalTranslateX = centerOffset.x + translateX;
      const finalTranslateY = centerOffset.y + translateY;

      return `translate(${finalTranslateX}px, ${finalTranslateY}px) scale(${scale}) rotate(${rotation}deg)`;
    }

    // No centering - use direct transforms
    return `translate(${translateX}px, ${translateY}px) scale(${scale}) rotate(${rotation}deg)`;
  };

  /**
   * Get transform including centering offset (for coordinate calculations)
   */
  const getTransformWithCenter = (): TransformState => {
    const centerOffset = getBaseCenterOffset();
    return {
      ...currentTransform,
      translateX: currentTransform.translateX + centerOffset.x,
      translateY: currentTransform.translateY + centerOffset.y,
    };
  };

  /**
   * Force recalculation of base center offset (call when content or stage dimensions change)
   */
  const recalculateCenter = (): void => {
    isCenterCalculated = false;
    calculateBaseCenterOffset();
  };

  /**
   * Cleanup function
   */
  const destroy = (): void => {
    listeners.length = 0;
  };

  return {
    getTransform,
    getTransformWithCenter,
    setTransform,
    resetTransform,
    onTransformChange,
    toCSSTransform,
    recalculateCenter,
    destroy,
  };
};

/**
 * Coordinate conversion utilities
 * @param getTransform - Function to get current transform state
 * @param getBoundingRect - Function to get element bounding rect
 */
export const createCoordinateConverter = (
  getTransform: () => TransformState,
  getBoundingRect: () => DOMRect
) => {
  /**
   * Convert screen coordinates to stage coordinates
   * @param point - Point in screen space
   * @returns Point in stage space
   */
  const screenToStage = (point: Point): Point => {
    const { scale, translateX, translateY } = getTransform();
    const rect = getBoundingRect();

    // Convert screen point to local coordinates
    const localX = point.x - rect.left;
    const localY = point.y - rect.top;

    // Apply inverse transform
    const stageX = (localX - translateX) / scale;
    const stageY = (localY - translateY) / scale;

    return { x: stageX, y: stageY };
  };

  /**
   * Convert stage coordinates to screen coordinates
   * @param point - Point in stage space
   * @returns Point in screen space
   */
  const stageToScreen = (point: Point): Point => {
    const { scale, translateX, translateY } = getTransform();
    const rect = getBoundingRect();

    // Apply transform
    const localX = point.x * scale + translateX;
    const localY = point.y * scale + translateY;

    // Convert to screen coordinates
    const screenX = localX + rect.left;
    const screenY = localY + rect.top;

    return { x: screenX, y: screenY };
  };

  return {
    screenToStage,
    stageToScreen,
  };
};

/**
 * Transform manipulation utilities
 */
export const TransformUtils = {
  /**
   * Zoom by a factor around a specific point
   * @param currentTransform - Current transform state
   * @param factor - Zoom factor (1.2 = 20% zoom in, 0.8 = 20% zoom out)
   * @param centerPoint - Point to zoom around (in screen space)
   * @param getBoundingRect - Function to get element bounds
   */
  zoomAtPoint: (
    currentTransform: TransformState,
    factor: number,
    centerPoint: Point,
    getBoundingRect: () => DOMRect
  ): Partial<TransformState> => {
    const rect = getBoundingRect();
    const { scale, translateX, translateY } = currentTransform;

    // Convert center point to local coordinates
    const localX = centerPoint.x - rect.left;
    const localY = centerPoint.y - rect.top;

    // Calculate new scale
    const newScale = scale * factor;

    // Adjust translation to keep the zoom center point fixed
    const newTranslateX = localX - (localX - translateX) * factor;
    const newTranslateY = localY - (localY - translateY) * factor;

    return {
      scale: newScale,
      translateX: newTranslateX,
      translateY: newTranslateY,
    };
  },

  /**
   * Simple zoom in/out from center
   * @param currentTransform - Current transform state
   * @param factor - Zoom factor
   */
  zoom: (
    currentTransform: TransformState,
    factor: number
  ): Partial<TransformState> => {
    return {
      scale: currentTransform.scale * factor,
    };
  },

  /**
   * Pan by a delta amount
   * @param currentTransform - Current transform state
   * @param deltaX - X offset to add
   * @param deltaY - Y offset to add
   */
  pan: (
    currentTransform: TransformState,
    deltaX: number,
    deltaY: number
  ): Partial<TransformState> => {
    return {
      translateX: currentTransform.translateX + deltaX,
      translateY: currentTransform.translateY + deltaY,
    };
  },

  /**
   * Clamp transform values to reasonable bounds
   * @param transform - Transform to clamp
   * @param bounds - Optional bounds for clamping
   */
  clamp: (
    transform: TransformState,
    bounds: {
      minScale?: number;
      maxScale?: number;
      maxTranslate?: number;
    } = {}
  ): TransformState => {
    const { minScale = 0.1, maxScale = 10, maxTranslate = 5000 } = bounds;

    return {
      scale: Math.max(minScale, Math.min(maxScale, transform.scale)),
      translateX: Math.max(
        -maxTranslate,
        Math.min(maxTranslate, transform.translateX)
      ),
      translateY: Math.max(
        -maxTranslate,
        Math.min(maxTranslate, transform.translateY)
      ),
      rotation: transform.rotation % 360,
    };
  },
};
